/**
 * Exportação de diagnóstico (F6-T14, F20-T03/T04).
 *
 * O app roda offline e sem canal de suporte. Quando algo dá errado, o usuário precisa
 * conseguir copiar um retrato do estado interno. Este relatório é deliberadamente
 * **anônimo**: contagens, versões e status — nunca nomes de sinais favoritados nem
 * qualquer coisa que descreva o comportamento pessoal de estudo (PLAN §35).
 */
import { db } from '@/db/schema';
import { countByStatus } from '@/db/repositories/downloads';
import { getCatalogMetadata } from '@/db/repositories/catalog';

export interface DiagnosticsReport {
  generatedAt: string;
  app: {
    userAgent: string;
    language: string;
    standalone: boolean;
    online: boolean;
  };
  catalog: {
    present: boolean;
    schemaVersion?: number;
    catalogVersion?: string;
    contentHash?: string;
    appliedAt?: string;
    lastCheckedAt?: string;
    categories: number;
    signs: number;
    inactiveSigns: number;
  };
  personal: {
    statesStored: number;
    favorites: number;
    learned: number;
    studySessions: number;
  };
  downloads: Record<string, number>;
  storage: {
    persisted: boolean | null;
    usageBytes: number | null;
    quotaBytes: number | null;
  };
  capabilities: {
    serviceWorker: boolean;
    opfs: boolean;
    storageManager: boolean;
    indexedDb: boolean;
  };
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS usa a propriedade proprietária; o resto usa display-mode.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

async function readStorage(): Promise<DiagnosticsReport['storage']> {
  if (typeof navigator === 'undefined' || !navigator.storage) {
    return { persisted: null, usageBytes: null, quotaBytes: null };
  }

  const [persisted, estimate] = await Promise.all([
    navigator.storage.persisted?.().catch(() => null) ?? Promise.resolve(null),
    navigator.storage.estimate?.().catch(() => null) ?? Promise.resolve(null),
  ]);

  return {
    persisted: persisted ?? null,
    usageBytes: estimate?.usage ?? null,
    quotaBytes: estimate?.quota ?? null,
  };
}

export async function buildDiagnosticsReport(): Promise<DiagnosticsReport> {
  const metadata = await getCatalogMetadata();

  const [categories, signs, inactiveSigns, statesStored, favorites, learned, studySessions] =
    await Promise.all([
      db.categories.count(),
      db.signs.count(),
      db.signs.filter((sign) => !sign.active).count(),
      db.localSignState.count(),
      db.localSignState.filter((state) => state.favorite).count(),
      db.localSignState.filter((state) => state.learned).count(),
      db.studySessions.count(),
    ]);

  return {
    generatedAt: new Date().toISOString(),
    app: {
      userAgent: typeof navigator === 'undefined' ? 'desconhecido' : navigator.userAgent,
      language: typeof navigator === 'undefined' ? 'desconhecido' : navigator.language,
      standalone: isStandalone(),
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
    },
    catalog: {
      present: metadata !== undefined,
      ...(metadata
        ? {
            schemaVersion: metadata.schemaVersion,
            catalogVersion: metadata.catalogVersion,
            contentHash: metadata.contentHash,
            appliedAt: metadata.appliedAt,
            ...(metadata.lastCheckedAt ? { lastCheckedAt: metadata.lastCheckedAt } : {}),
          }
        : {}),
      categories,
      signs,
      inactiveSigns,
    },
    personal: { statesStored, favorites, learned, studySessions },
    downloads: await countByStatus(),
    storage: await readStorage(),
    capabilities: {
      serviceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
      opfs: typeof navigator !== 'undefined' && 'storage' in navigator && 'getDirectory' in navigator.storage,
      storageManager: typeof navigator !== 'undefined' && 'storage' in navigator,
      indexedDb: typeof indexedDB !== 'undefined',
    },
  };
}

export async function diagnosticsAsText(): Promise<string> {
  return JSON.stringify(await buildDiagnosticsReport(), null, 2);
}
